// The ingredient line under a recipe's name on the crafting panel: which materials it costs, how many
// of each the player holds, and -- the part that was broken -- WHICH ONE is stopping them.
//
// The card used to pick that line by reading the server's hover prose back and taking the first entry
// it found:
//
//     recipe.disabledReason?.find(r => r.startsWith("-"))?.replace(/^-\s*/, "need ")
//
// The prose lists ingredients in recipe order, so for a rabbit snare (3 sticks and a rope) a player
// carrying ten sticks and no rope was shown "need 3 stick (have 10)" -- a line that is both satisfied
// and nonsense as a reason, with the actual blocker never drawn at all. The fix is not a better
// regex: the panel is now handed the price as data (`IngredientCost[]`, straight off the same pass
// that greyed the Craft button) and decides for itself.
//
// Pure of pixi on purpose, like crafting_layout.ts beside it -- see __tests__/crafting_ingredients.test.ts.

// Mirrors game-common's IngredientCost. Restated rather than imported so this module stays free of
// the item/mechanics import cycle; the shapes are checked against each other in the test.
export interface IngredientCost {
    itemType: string
    required: number
    held: number
    // Does this material have a recipe of its own? Decides whether a shortfall offers a jump to that
    // recipe or a line saying where to find the stuff.
    craftable?: boolean
    source?: string
    // The server's sealed conversation response that opens this material's recipe. Opaque here.
    jumpEvent?: any
}

export const CHIP_SEPARATOR = "  ·  "

export const isShort = (cost: IngredientCost): boolean => cost.held < cost.required

/**
 * One chip: `rope 0/1`.
 *
 * `held` is CLAMPED to what the recipe asks for, so a satisfied ingredient reads as a filled
 * requirement (`stick 3/3`) rather than an arithmetic puzzle (`stick 10/3`). The true count is still
 * in the row's hover text, which is where a player goes to ask "how many do I actually have".
 */
export const chipText = (cost: IngredientCost): string =>
    `${Math.min(cost.held, cost.required)}/${cost.required} ${cost.itemType}`

/**
 * The chips in drawing order: every ingredient the player is SHORT of first, then the rest, each
 * group keeping the recipe's own order.
 *
 * Short-first is the whole point. The line is one row tall and usually too narrow for a three-
 * ingredient recipe, so something has to be dropped -- and the thing that must never be dropped is
 * the reason the button is dead.
 */
export const orderedCosts = (cost: IngredientCost[]): IngredientCost[] => [
    ...cost.filter(isShort),
    ...cost.filter(next => !isShort(next)),
]

/**
 * One drawn chip: its text, whether it is a shortfall, and what tapping it should do.
 *
 * Chips are laid out and hit-tested INDIVIDUALLY rather than merged into coloured runs, because a
 * short ingredient is now a control: tapping "0/1 rope" opens the rope recipe, and tapping a material
 * nothing makes says where to find it. A merged run cannot carry either.
 */
export interface IngredientChip {
    text: string
    missing: boolean
    // Separator drawn before this chip. The chips are drawn as separate Texts butted together, so the
    // gap has to belong to one of them; it belongs to the one that follows.
    lead: string
    // Set only on a shortfall. Either a recipe to jump to, or a line saying where the stuff comes
    // from -- never both, because a material with a recipe already has its next step.
    actionable: boolean
    source?: string
    // Set ONLY on the `+N` tail: how many ingredients it stands in for. The tail is not an ingredient
    // -- it is a control that opens the whole recipe (see recipe_popover.ts) -- and it is flagged
    // rather than recognised by its text, because the caller walks these chips alongside the ordered
    // costs and the tail is the one chip that has no cost behind it.
    hidden?: number
}

/**
 * The ingredient line, trimmed to `maxChars`.
 *
 * Whatever does not fit is reported as a `+N` tail rather than silently dropped. At least one chip is
 * always emitted even if it overruns: a card that has room for nothing still has to say what is
 * missing, and an overlong line is a legible failure where an empty one is the bug this replaces.
 *
 * The tail carries its own count in `hidden`, because it is a CONTROL: tapping it opens the whole
 * recipe in a popover. A `+2` that a player cannot expand is a line that admits it is hiding
 * something and then refuses to say what -- and with short ingredients ordered first, a `+N` can
 * perfectly well be hiding a shortfall of its own on a three-ingredient recipe with room for one chip.
 */
export const ingredientChipsFor = (cost: IngredientCost[], maxChars: number): IngredientChip[] => {
    const ordered = orderedCosts(cost || [])
    if (ordered.length < 1) {
        return []
    }

    const taken: IngredientCost[] = []
    let width = 0
    ordered.forEach(next => {
        const remaining = ordered.length - taken.length - 1
        // What a `+N` tail would cost if this chip is the last one drawn.
        const tail = remaining > 0 ? CHIP_SEPARATOR.length + `+${remaining}`.length : 0
        const added = (taken.length > 0 ? CHIP_SEPARATOR.length : 0) + chipText(next).length
        if (taken.length > 0 && width + added + tail > maxChars) {
            return
        }
        taken.push(next)
        width += added
    })

    const chips: IngredientChip[] = taken.map((next, i) => ({
        text: chipText(next),
        missing: isShort(next),
        lead: i > 0 ? CHIP_SEPARATOR : "",
        // Only a SHORT ingredient is a question worth answering. One already covered is not.
        actionable: isShort(next) && !!next.craftable,
        source: isShort(next) && !next.craftable ? next.source : undefined,
    }))

    const hidden = ordered.length - taken.length
    if (hidden > 0) {
        chips.push({ text: `+${hidden}`, missing: false, lead: CHIP_SEPARATOR, actionable: false, hidden })
    }

    return chips
}

/**
 * The same line from the server's PROSE, for a payload that carried no structured cost.
 *
 * costText marks an ingredient the player is short of with `!` and the rest with `-`, so the blocker
 * is findable without parsing the counts back out. Kept narrow on purpose: this is the fallback, and
 * the only thing it has to get right is not naming a satisfied ingredient as the reason.
 */
export const reasonFromProse = (lines?: string[]): string | undefined => {
    const short = (lines || []).find(line => line.startsWith("!"))
    if (short) {
        return short.replace(/^!\s*/, "need ")
    }
    // No `!` anywhere: either every ingredient is covered (so the row is dead for some other reason,
    // like a full pack) or this is a one-line message such as "Inventory full, cannot craft".
    return (lines || []).find(line => !line.startsWith("-") && !line.endsWith(":"))
}
